import React, { useState, useContext, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
    useService as useAudioDocumentService,
    AudioDocumentStatus,
    AudioDocument,
} from "../../services/AudioDocuments";
import { Context as AuthContext } from "../../context/Auth";
import AudioPlayer from "../../components/AudioPlayer";
import ErrorAlert from "../../components/ErrorAlert";

export default function AudioDocumentDetail() {
    const { id } = useParams<{ id: string }>();
    const audioService = useAudioDocumentService();
    const { state: authState } = useContext(AuthContext);
    const navigate = useNavigate();

    const isAuthenticated = Boolean(authState.user);
    const [loading, setLoading] = useState(true);
    const [document, setDocument] = useState<AudioDocument | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [audioDuration, setAudioDuration] = useState(0);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);

    const fetchDocument = useCallback(async () => {
        if (!id || !isAuthenticated) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const doc = await audioService.get(id);
            setDocument(doc);
        } catch (err: any) {
            console.error("Failed to fetch audio document:", err);
            setError("Failed to load audio document");
        } finally {
            setLoading(false);
        }
    }, [id, audioService, isAuthenticated]);

    const loadAudio = useCallback(async () => {
        if (!document || !document.finalAudioS3Key) {
            return;
        }

        if (document.status !== AudioDocumentStatus.READY) {
            return;
        }

        try {
            setIsLoadingAudio(true);
            const streamResponse = await audioService.getStream(document.id);
            setAudioUrl(streamResponse.url);
            setAudioDuration(streamResponse.durationSeconds || 0);
        } catch (err) {
            console.error("Failed to load audio:", err);
        } finally {
            setIsLoadingAudio(false);
        }
    }, [document, audioService]);

    useEffect(() => {
        fetchDocument();
    }, [fetchDocument]);

    useEffect(() => {
        if (document && document.status === AudioDocumentStatus.READY) {
            loadAudio();
        }
    }, [document, loadAudio]);

    if (loading) {
        return (
            <Container
                disableGutters
                sx={{
                    width: "100%",
                    mx: "auto",
                    maxWidth: {
                        xs: "100%",
                        sm: 720,
                        md: 900,
                    },
                    mt: { xs: 1, sm: 2 },
                    pb: { xs: 2, sm: 3 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        py: 8,
                    }}
                >
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error || !document) {
        return (
            <Container
                disableGutters
                sx={{
                    width: "100%",
                    mx: "auto",
                    maxWidth: {
                        xs: "100%",
                        sm: 720,
                        md: 900,
                    },
                    mt: { xs: 1, sm: 2 },
                    pb: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ mb: 2 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/dashboard/library")}
                    >
                        Back to Library
                    </Button>
                </Box>
                {error && <ErrorAlert message={error} />}
            </Container>
        );
    }

    return (
        <Container
            disableGutters
            sx={{
                width: "100%",
                mx: "auto",
                maxWidth: {
                    xs: "100%",
                    sm: 720,
                    md: 900,
                },
                mt: { xs: 1, sm: 2 },
                pb: { xs: 2, sm: 3 },
            }}
        >
            <Box>
                {/* Back button */}
                <Box sx={{ mb: 2 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/dashboard/library")}
                    >
                        Back to Library
                    </Button>
                </Box>

                {/* Document text content */}
                <Box
                    sx={{
                        mb: 4,
                        p: 3,
                        borderRadius: 2,
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        minHeight: "400px",
                        color: "text.primary",
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            lineHeight: 1.8,
                            color: "text.primary",
                        }}
                    >
                        {document.text}
                    </Typography>
                </Box>

                {/* Audio player at bottom */}
                {document.status === AudioDocumentStatus.READY && audioUrl && (
                    <Box
                        sx={{
                            position: "sticky",
                            bottom: 0,
                            pt: 2,
                            pb: 2,
                            zIndex: 1000,
                        }}
                    >
                        {isLoadingAudio ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    py: 2,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <AudioPlayer
                                src={audioUrl}
                                duration={audioDuration}
                            />
                        )}
                    </Box>
                )}

                {document.status === AudioDocumentStatus.PROCESSING && (
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 4,
                            color: "text.secondary",
                        }}
                    >
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="body2">
                            Audio is being processed. Please check back soon.
                        </Typography>
                    </Box>
                )}

                {document.status === AudioDocumentStatus.FAILED && (
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 4,
                        }}
                    >
                        <ErrorAlert
                            message={
                                document.errorMessage ||
                                "Audio processing failed. Please try creating a new document."
                            }
                        />
                    </Box>
                )}
            </Box>
        </Container>
    );
}
